import React from 'react'
import { datadogRum } from '@datadog/browser-rum'
import LogoHorizontal from '@/icons/logo-horizontal.svg'

// Fallback stays in English. The outermost ErrorBoundary in app.tsx wraps the
// Inertia provider itself, so when it falls through to this default the
// Inertia context isn't available — anything calling useTranslations / usePage
// here would double-crash the page.
function DefaultErrorFallback() {
  return (
    <div className="bg-indigoBlue min-h-screen">
      <a href="/">
        <div className="bg-indigoBlue py-3 px-5 lg:px-9 lg:py-5">
          <div className="w-36 lg:w-44">
            <LogoHorizontal className="text-white" />
          </div>
        </div>
      </a>
      <div className="xl:w-[1200px] mx-auto py-14 px-5 lg:px-9">
        <div className="flex lg:justify-end bg-[url('https://images.ctfassets.net/7rncvj1f8mw7/3hFwAbRp0pdfStyL1jNUXW/3713a16599af668b2d36d4472a1c6540/security-cellar-new.svg')] bg-contain bg-no-repeat bg-center  min-h-[360px] lg:min-h-[690px] items-center">
          <div className="text-white lg:w-7/12">
            <p className="text-2xl lg:text-4xl mb-0">Oops.</p>
            <h1 className="my-6 header-gradient-reset">Something went wrong!</h1>
            <p className="text-2xl lg:text-4xl">
              It's not your fault its ours, we're working to fix the issue. Please try again later.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

interface ErrorBoundaryProps {
  children: React.ReactNode
  fallback?: React.ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static getDerivedStateFromError(_error: Error): ErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(error, errorInfo)
    datadogRum.addError(error, {
      componentStack: errorInfo.componentStack,
      source: 'react-error-boundary',
    })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback !== undefined) {
        return this.props.fallback
      }

      return <DefaultErrorFallback />
    }

    return this.props.children
  }
}

export default ErrorBoundary
